import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Space, message, Input, Tag, Tooltip, Drawer,
  Form, InputNumber, Row, Col, Modal, Popconfirm, Alert, Empty
} from 'antd';
import {
  ReloadOutlined, EditOutlined, CopyOutlined, DeleteOutlined,
  SaveOutlined, EyeOutlined, PlusOutlined, FileTextOutlined, StarFilled, StarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import authService from '../utils/auth';
import RichTextEditor from '../components/RichTextEditor';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const formatDate = (d) => d ? dayjs(d).format('YYYY-MM-DD') : '-';

export default function VoucherTemplateEdit() {
  const userInfo = authService.getUserInfo();
  const canEdit = userInfo?.role === 'admin' || userInfo?.role === 'operations';

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');

  // Drawer 状态
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create'); // create | edit
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingIsDefault, setEditingIsDefault] = useState(false);
  const [editorForm] = Form.useForm();
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  // 关联团 — 在 Drawer 内
  const [allTours, setAllTours] = useState([]);
  const [selectedTourIds, setSelectedTourIds] = useState([]);
  const [tourSearchText, setTourSearchText] = useState('');

  // ─── 列表 ──────────────────────────────────────────────────────────────

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (searchText) params.search = searchText;
      const res = await axios.get(`${API_BASE}/vouchers/templates`, {
        params, headers: authService.getAuthHeaders()
      });
      setList(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      message.error('获取模板列表失败：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchText]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const fetchAllTours = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/tours`, {
        headers: authService.getAuthHeaders()
      });
      setAllTours(res.data.data || []);
    } catch (err) { /* ignore */ }
  }, []);

  // ─── Drawer ─────────────────────────────────────────────────────────────

  const openCreate = async () => {
    setDrawerMode('create');
    setEditingId(null);
    setEditingName('');
    setEditingIsDefault(false);
    setSelectedTourIds([]);
    setTourSearchText('');
    setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return ''; });
    editorForm.resetFields();
    editorForm.setFieldsValue({
      name: '',
      issueReason: '因航空公司航班变动 (Schedule Change) 导致原行程无法按计划执行，特此签发本旅游优惠券作为补偿。',
      validDays: 365,
      amount: 1099,
      companyName: '万国旅行社',
      companyNameEn: 'Worldwide Holidays',
      codePrefix: 'WGV',
      termsContent: ''
    });
    setDrawerOpen(true);
    fetchAllTours();
  };

  const openEdit = async (record) => {
    setDrawerMode('edit');
    setEditingId(record.id);
    setEditingName(record.name);
    setEditingIsDefault(record.isDefault);
    setTourSearchText('');
    setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return ''; });
    setDrawerOpen(true);
    setDrawerLoading(true);
    fetchAllTours();
    try {
      const res = await axios.get(`${API_BASE}/vouchers/templates/${record.id}`, {
        headers: authService.getAuthHeaders()
      });
      const t = res.data || {};
      editorForm.setFieldsValue(t);
      setSelectedTourIds((t.tours || []).map(x => x.id));
    } catch (err) {
      message.error('加载模板失败');
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); }
  };

  const handleSave = async () => {
    if (!canEdit) return message.warning('仅管理员/运营可修改');
    try {
      const values = await editorForm.validateFields();
      setSaving(true);
      const payload = { ...values, tourIds: selectedTourIds };
      if (drawerMode === 'create') {
        await axios.post(`${API_BASE}/vouchers/templates`, payload, {
          headers: authService.getAuthHeaders()
        });
        message.success('已创建');
      } else {
        await axios.put(`${API_BASE}/vouchers/templates/${editingId}`, payload, {
          headers: authService.getAuthHeaders()
        });
        message.success('已保存');
      }
      closeDrawer();
      fetchList();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('保存失败：' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewing(true);
      const values = editorForm.getFieldsValue();
      const res = await axios.post(
        `${API_BASE}/vouchers/templates/preview`,
        values,
        { headers: authService.getAuthHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
    } catch (err) {
      message.error('预览失败');
    } finally {
      setPreviewing(false);
    }
  };

  // ─── 行操作 ────────────────────────────────────────────────────────────

  const handleDelete = async (record) => {
    try {
      await axios.delete(`${API_BASE}/vouchers/templates/${record.id}`, {
        headers: authService.getAuthHeaders()
      });
      message.success('已删除');
      fetchList();
    } catch (err) {
      message.error('删除失败：' + (err.response?.data?.error || err.message));
    }
  };

  const handleCopy = async (record) => {
    try {
      await axios.post(`${API_BASE}/vouchers/templates/${record.id}/copy`, {}, {
        headers: authService.getAuthHeaders()
      });
      message.success('已复制');
      fetchList();
    } catch (err) {
      message.error('复制失败：' + (err.response?.data?.error || err.message));
    }
  };

  const handleSetDefault = async (record) => {
    try {
      await axios.post(`${API_BASE}/vouchers/templates/${record.id}/set-default`, {}, {
        headers: authService.getAuthHeaders()
      });
      message.success(`已将「${record.name}」设为默认`);
      fetchList();
    } catch (err) {
      message.error('设置失败：' + (err.response?.data?.error || err.message));
    }
  };

  // ─── 关联团选择（Drawer 内表格） ──────────────────────────────────────────

  const filteredTours = (() => {
    const todayStart = dayjs().startOf('day').valueOf();
    const selectedSet = new Set(selectedTourIds);
    return allTours
      // 已选的团始终保留（即使过期/搜索不匹配也要显示）；其他团仅显示未出团
      .filter(t => {
        if (selectedSet.has(t.id)) return true;
        return t.departureDate && dayjs(t.departureDate).valueOf() >= todayStart;
      })
      .filter(t => {
        if (selectedSet.has(t.id)) return true; // 已选不被搜索过滤掉
        if (!tourSearchText) return true;
        const s = tourSearchText.toLowerCase();
        return (t.productName || '').toLowerCase().includes(s) ||
               String(t.id).includes(s) ||
               formatDate(t.departureDate).includes(s);
      })
      .sort((a, b) => {
        // 已选的排前面
        const aSel = selectedSet.has(a.id) ? 0 : 1;
        const bSel = selectedSet.has(b.id) ? 0 : 1;
        if (aSel !== bSel) return aSel - bSel;
        return new Date(a.departureDate) - new Date(b.departureDate);
      });
  })();

  const tourSelectionColumns = [
    {
      title: '产品名称',
      key: 'destinationName',
      render: (_, r) => r.destination?.name || '-'
    },
    { title: '出团日期', dataIndex: 'departureDate', width: 110, render: formatDate },
    {
      title: '当前模板', key: 'current', width: 160,
      render: (_, r) => {
        if (!r.voucherTemplateId) return <Tag>全局默认</Tag>;
        if (r.voucherTemplateId === editingId) return <Tag color="blue">本模板</Tag>;
        return <Tag color="orange">其他模板 #{r.voucherTemplateId}</Tag>;
      }
    }
  ];

  // ─── 主表格列 ──────────────────────────────────────────────────────────

  const columns = [
    {
      title: '模板名称',
      dataIndex: 'name',
      render: (v, r) => (
        <Space>
          <a onClick={() => openEdit(r)} style={{ fontWeight: 500 }}>{v}</a>
          {r.isDefault && <Tag icon={<StarFilled />} color="gold">全局默认</Tag>}
        </Space>
      )
    },
    {
      title: '关联团数',
      dataIndex: 'tourCount',
      width: 110,
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{v || 0}</span>
    },
    {
      title: '签发原因摘要',
      dataIndex: 'issueReason',
      ellipsis: true,
      render: (v) => v ? <Tooltip title={v}>{v.slice(0, 60)}{v.length > 60 ? '...' : ''}</Tooltip> : '-'
    },
    {
      title: '有效期',
      dataIndex: 'validDays',
      width: 90,
      render: (v) => `${v || 365} 天`
    },
    {
      title: '编号前缀',
      dataIndex: 'codePrefix',
      width: 110,
      render: (v) => <span style={{ fontFamily: 'monospace' }}>{v}</span>
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      width: 150,
      render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 280,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="编辑">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          </Tooltip>
          {canEdit && (
            <>
              <Tooltip title="复制（生成同样配置的新模板）">
                <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(r)}>复制</Button>
              </Tooltip>
              {!r.isDefault && (
                <Tooltip title="设为默认">
                  <Button size="small" icon={<StarOutlined />} onClick={() => handleSetDefault(r)}>设默认</Button>
                </Tooltip>
              )}
              {!r.isDefault && (
                <Popconfirm
                  title="确认删除该模板？"
                  description={`关联的 ${r.tourCount || 0} 个团将回退到全局默认`}
                  okType="danger"
                  onConfirm={() => handleDelete(r)}
                >
                  <Tooltip title="删除">
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              )}
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 16 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="自建 Voucher 模板，按需关联到具体团号"
        description="新建模板 → 选择应用到哪些团（可多选）→ 保存。一个团只能属于一个模板，未关联的团使用「全局默认」。已签发的 Voucher 用签发时刻的快照，不受后续修改影响。"
      />

      <Card
        title={<Space><FileTextOutlined />Voucher 模板</Space>}
        extra={
          <Space>
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                新建模板
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={fetchList}>刷新</Button>
          </Space>
        }
      >
        <Form layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item label="搜索">
            <Input.Search
              placeholder="模板名称"
              allowClear
              style={{ width: 240 }}
              onSearch={(v) => { setSearchText(v || ''); setPage(1); }}
            />
          </Form.Item>
        </Form>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={list}
          size="small"
          scroll={{ x: 1200 }}
          locale={{ emptyText: <Empty description="还没有模板，点「新建模板」开始" /> }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 个模板`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); }
          }}
        />
      </Card>

      {/* 编辑 Drawer */}
      <Drawer
        title={
          <Space>
            {drawerMode === 'create' ? '新建 Voucher 模板' : `编辑 Voucher 模板 · ${editingName}`}
            {editingIsDefault && <Tag icon={<StarFilled />} color="gold">全局默认</Tag>}
          </Space>
        }
        open={drawerOpen}
        onClose={closeDrawer}
        width={1200}
        destroyOnClose
        extra={
          <Space>
            <Button icon={<EyeOutlined />} loading={previewing} onClick={handlePreview}>预览 PDF</Button>
            {canEdit && (
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                保存
              </Button>
            )}
          </Space>
        }
      >
        <Row gutter={16} style={{ height: '100%' }}>
          <Col span={14}>
            {!canEdit && (
              <Alert
                type="info" showIcon style={{ marginBottom: 16 }}
                message="您当前角色为只读模式（仅 admin/operations 可修改）"
              />
            )}

            <Form form={editorForm} layout="vertical" disabled={!canEdit || drawerLoading}>
              <Form.Item name="name" label="模板名称" rules={[{ required: true, message: '请填写模板名称' }]}>
                <Input placeholder="如：南岛航变补偿券" />
              </Form.Item>

              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item
                    name="amount"
                    label="Voucher 面额（NZD）"
                    rules={[{ required: true, message: '请填写面额' }]}
                  >
                    <InputNumber min={0} step={0.01} precision={2} prefix="$" style={{ width: '100%' }} placeholder="1099.00" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="codePrefix" label="编号前缀" tooltip="如 WGV，最终编号：WGV202600001（不含分隔符方便输入）">
                    <Input placeholder="WGV" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="validDays" label="有效期（天）" rules={[{ required: true, message: '请填写' }]}>
                    <InputNumber min={1} max={3650} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="issueReason" label="签发原因（PDF 上显示的统一文案）">
                <Input.TextArea rows={3} placeholder="如：因航空公司航班变动 (CA783) 导致原行程取消..." />
              </Form.Item>

              <Form.Item name="termsContent" label="使用条款">
                <RichTextEditor placeholder="请输入使用条款" style={{ minHeight: 220 }} />
              </Form.Item>
            </Form>

            {/* 关联团号 */}
            <Card
              size="small"
              title={<Space>关联团号 <Tag color="blue">已选 {selectedTourIds.length}</Tag></Space>}
              style={{ marginTop: 8 }}
              bodyStyle={{ padding: 12 }}
            >
              {/* 已选团清单（独立显示，方便用户随时看到选了什么） */}
              {selectedTourIds.length > 0 && (
                <div style={{
                  marginBottom: 8,
                  padding: '8px 10px',
                  background: '#e6f4ff',
                  border: '1px solid #91caff',
                  borderRadius: 4
                }}>
                  <div style={{ fontSize: 12, color: '#1677ff', marginBottom: 6, fontWeight: 500 }}>
                    已关联的团（{selectedTourIds.length}）
                  </div>
                  <Space size={[6, 6]} wrap>
                    {selectedTourIds.map(tid => {
                      const t = allTours.find(x => x.id === tid);
                      const label = t
                        ? `${t.destination?.name || '-'} · ${formatDate(t.departureDate)}`
                        : `团 #${tid}`;
                      return (
                        <Tag
                          key={tid}
                          closable={canEdit}
                          color="blue"
                          onClose={(e) => {
                            e.preventDefault();
                            setSelectedTourIds(selectedTourIds.filter(x => x !== tid));
                          }}
                          style={{ marginRight: 0 }}
                        >
                          {label}
                        </Tag>
                      );
                    })}
                  </Space>
                </div>
              )}

              <Input.Search
                placeholder="搜索：团号 / 产品名称 / 日期"
                allowClear
                onChange={(e) => setTourSearchText(e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <Table
                rowKey="id"
                size="small"
                dataSource={filteredTours}
                columns={tourSelectionColumns}
                pagination={{ pageSize: 8, size: 'small' }}
                scroll={{ y: 240 }}
                rowSelection={{
                  selectedRowKeys: selectedTourIds,
                  onChange: setSelectedTourIds,
                  preserveSelectedRowKeys: true
                }}
              />
            </Card>
          </Col>

          <Col span={10}>
            <Card
              title="PDF 预览"
              size="small"
              extra={<Button size="small" icon={<EyeOutlined />} loading={previewing} onClick={handlePreview}>刷新</Button>}
              bodyStyle={{ padding: 0, height: 'calc(100vh - 200px)', overflow: 'hidden' }}
            >
              {previewUrl ? (
                <iframe
                  title="voucher-preview"
                  src={previewUrl}
                  style={{ width: '100%', height: '100%', border: 0 }}
                />
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
                  点击「预览 PDF」查看效果
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Drawer>
    </div>
  );
}
